import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, Search, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react'
import { api, formatMoney } from '@/lib/api'

type Produto = {
  id: number
  codigo: string
  descricao: string
  estoque_atual: number
  preco_custo: number
  preco_venda: number
  custo_medio: number
}

export default function GiroProdutosPage() {
  const [busca, setBusca] = useState('')

  const { data: produtos = [], isLoading } = useQuery<Produto[]>({
    queryKey: ['produtos-list'],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { finalidade: 'revenda', limit: 1000 } })).data,
  })

  // V1 simples: usa estoque_atual + custo_medio. Próxima versão integra com vendas pra calcular giro real.
  const lista = useMemo(() => {
    const q = busca.toLowerCase().trim()
    const f = produtos.filter((p: any) =>
      p.tipo === 'simples' || p.tipo === 'variacao'
    ).filter((p: any) =>
      !q || (p.codigo || '').toLowerCase().includes(q) || (p.descricao || '').toLowerCase().includes(q)
    )
    return f.map((p: any) => {
      const estoque = Number(p.estoque_atual || 0)
      const custo = Number(p.custo_medio || p.preco_custo || 0)
      const venda = Number(p.preco_venda || 0)
      const valor_estoque = estoque * custo
      const margem_pct = venda > 0 && custo > 0 ? ((venda - custo) / venda) * 100 : 0
      const status = estoque <= 0 ? 'ruptura' : estoque < 5 ? 'baixo' : estoque > 200 ? 'alto' : 'normal'
      return { ...p, estoque, custo, venda, valor_estoque, margem_pct, status }
    }).sort((a: any, b: any) => b.valor_estoque - a.valor_estoque)
  }, [produtos, busca])

  const total_valor = lista.reduce((s: number, p: any) => s + p.valor_estoque, 0)
  const ruptura = lista.filter((p: any) => p.status === 'ruptura').length
  const alto = lista.filter((p: any) => p.status === 'alto').length

  if (isLoading) return <div className="p-12 text-center text-ink-400">Carregando...</div>

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <RefreshCw size={22} className="text-violet-600" /> Giro de Produtos
          </h1>
          <p className="text-xs text-ink-500">Curva ABC, valor em estoque, ruptura e excesso. V1 simples — análise por estoque atual.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-7 w-[260px]" placeholder="SKU ou descrição..." value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
        <strong>V1 — em breve:</strong> integração com lançamentos de venda pra calcular dias de cobertura, giro/mês,
        curva ABC real (Pareto), e alertas de ruptura/excesso baseado em histórico.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="border rounded p-3 bg-blue-50 border-blue-200">
          <div className="text-[10px] uppercase font-semibold text-blue-900">Total produtos</div>
          <div className="text-lg font-bold text-blue-900 font-mono">{lista.length}</div>
        </div>
        <div className="border rounded p-3 bg-violet-50 border-violet-200">
          <div className="text-[10px] uppercase font-semibold text-violet-900">Valor em estoque</div>
          <div className="text-lg font-bold text-violet-900 font-mono">{formatMoney(total_valor)}</div>
        </div>
        <div className="border rounded p-3 bg-red-50 border-red-200">
          <div className="text-[10px] uppercase font-semibold text-red-900">Em ruptura</div>
          <div className="text-lg font-bold text-red-900 font-mono">{ruptura}</div>
        </div>
        <div className="border rounded p-3 bg-amber-50 border-amber-200">
          <div className="text-[10px] uppercase font-semibold text-amber-900">Estoque alto (&gt;200)</div>
          <div className="text-lg font-bold text-amber-900 font-mono">{alto}</div>
        </div>
      </div>

      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-100">
            <tr className="text-[10px] uppercase font-semibold text-ink-600">
              <th className="text-left py-2 pl-3">SKU</th>
              <th className="text-left py-2">Produto</th>
              <th className="text-right py-2">Estoque</th>
              <th className="text-right py-2">Custo unit.</th>
              <th className="text-right py-2">Valor estoque</th>
              <th className="text-right py-2">Margem %</th>
              <th className="text-center py-2 pr-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {lista.slice(0, 100).map((p: any) => (
              <tr key={p.id} className="border-t border-ink-100 hover:bg-blue-50/30 text-xs">
                <td className="py-1.5 pl-3 font-mono text-[10px]">{p.codigo}</td>
                <td className="py-1.5 truncate max-w-[260px]">{p.descricao}</td>
                <td className="text-right py-1.5 font-mono">{p.estoque}</td>
                <td className="text-right py-1.5 font-mono">{formatMoney(p.custo)}</td>
                <td className="text-right py-1.5 font-mono font-semibold text-violet-700">{formatMoney(p.valor_estoque)}</td>
                <td className={`text-right py-1.5 font-mono ${p.margem_pct >= 30 ? 'text-emerald-700' : p.margem_pct >= 15 ? 'text-amber-700' : 'text-red-700'}`}>
                  {p.margem_pct.toFixed(1)}%
                </td>
                <td className="text-center py-1.5 pr-3">
                  {p.status === 'ruptura' && <span className="inline-flex items-center gap-1 text-[10px] text-red-700"><AlertTriangle size={10} /> Ruptura</span>}
                  {p.status === 'baixo' && <span className="inline-flex items-center gap-1 text-[10px] text-amber-700"><TrendingDown size={10} /> Baixo</span>}
                  {p.status === 'alto' && <span className="inline-flex items-center gap-1 text-[10px] text-orange-700"><TrendingUp size={10} /> Alto</span>}
                  {p.status === 'normal' && <span className="text-[10px] text-emerald-700">OK</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {lista.length > 100 && (
          <div className="p-2 text-xs text-ink-500 text-center bg-ink-50 border-t">
            Mostrando 100 primeiros de {lista.length}. Use busca pra filtrar.
          </div>
        )}
      </div>
    </div>
  )
}
